import { AddUserForm, EditPasswordForm, EditUserForm, ModalMode, User } from '@/views/users/users.types';
import { useGlobals }                                                   from '@/hooks/use-globals';
import { Computed }                                                     from '@/types';
import { computed }                                                     from '@vue/composition-api';
import { DrawerAttributes, DrawerPanelProps }                           from '@/components/drawer/drawer.types';
import userFormRules                                                    from '@/views/users/users.rules';
import { TranslateResult }                                              from 'vue-i18n';
import useGlobalTranslations                                            from '@/hooks/use-global-translations';

interface UseSidePanel {
  statusOptions: Computed<string[]>;
  modalMode: Computed<ModalMode>;
  fields: Computed<AddUserForm | EditUserForm | EditPasswordForm>;
  panelAttrs: Computed<DrawerAttributes>;
  drawerPanelProps: Computed<DrawerPanelProps>;
  rules: any;
  closePanel: () => void;
  handleUserFormInputChange: (event: string, key: string) => void;
  showAddUserFormPanel: () => void;
  showEditUserFormPanel: (user: User) => void;
  showEditPasswordFormPanel: (user: User) => Promise<void>;
}

export const useSidePanel = (): UseSidePanel => {
  const { store, i18n } = useGlobals();

  const statusOptions: Computed<string[]> = computed((): string[] => store.state.views.vUsers.statusOptions);
  const buttonSave: TranslateResult = useGlobalTranslations('buttons.save');
  const modalMode: Computed<ModalMode> = computed((): ModalMode => store.state.views.vUsers.modal.mode);
  const isModalOpened: Computed<boolean> = computed((): boolean => store.state.views.vUsers.modal.open);

  const fields: Computed<AddUserForm | EditUserForm | EditPasswordForm> = computed(() => {
    const { fields } = store.state.views.vUsers.modal.form;
    switch (modalMode.value) {
    case ModalMode.EDIT_USER:
      return {
        firstName: fields.firstName,
        lastName: fields.lastName,
        position: fields.position,
        email: fields.email,
        status: fields.status,
      };
    case ModalMode.EDIT_PASSWORD:
      return {
        password: fields.password,
        repeatPassword: fields.repeatPassword,
      };
    default:
      return fields;
    }
  });

  const panelAttrs: Computed<DrawerAttributes> = computed(() => ({
    visible: isModalOpened.value,
    size: '45rem',
    title: `${i18n.t(`vUsers.sidePanel.${modalMode.value}`)}`,
  }));

  const drawerPanelProps: Computed<DrawerPanelProps> = computed(() => ({
    button: buttonSave as string,
    isLoading: false,
    panelAttrs: panelAttrs.value,
  }));

  const rules = userFormRules(fields.value, modalMode.value);

  const closePanel = (): void => store.commit('views/vUsers/toggleModal', false);

  const handleUserFormInputChange = (event: string, key: string): void => {
    store.commit('views/vUsers/setFormInputs', { [ key ]: event });
  };

  const showAddUserFormPanel = (): void => {
    store.commit('views/vUsers/setEditMode', ModalMode.CREATE_USER);
    store.commit('views/vUsers/clearFormInputs');
    store.commit('views/vUsers/toggleModal', true);
  };

  const showEditUserFormPanel = ({ firstName, lastName, email, uuid, status, position }: User): void => {
    const userForm: EditUserForm = {
      firstName,
      lastName,
      status,
      email,
      position,
    };
    store.commit('views/vUsers/setSelectedUserUuid', uuid);
    store.commit('views/vUsers/setEditMode', ModalMode.EDIT_USER);
    store.commit('views/vUsers/setFormInputs', userForm);
    store.commit('views/vUsers/toggleModal', true);
  };

  const showEditPasswordFormPanel = async ({ uuid }: User): Promise<void> => {
    store.commit('views/vUsers/setSelectedUserUuid', uuid);
    store.commit('views/vUsers/setEditMode', ModalMode.EDIT_PASSWORD);
    store.commit('views/vUsers/clearFormInputs');
    store.commit('views/vUsers/toggleModal', true);
  };

  return {
    statusOptions,
    modalMode,
    fields,
    panelAttrs,
    drawerPanelProps,
    rules,
    closePanel,
    handleUserFormInputChange,
    showAddUserFormPanel,
    showEditUserFormPanel,
    showEditPasswordFormPanel,
  };
};
